import {
  AgentSummary,
  CreateFlightStatusAlertPayload,
  CreateHotelPriceAlertPayload,
  CurrentHotelPriceResponse,
  ProactiveAgentListResponse,
  ProactiveAgentRunsResponse,
  ProactiveAgentStatus,
  UpdateProactiveAgentPayload,
} from "@/types/agent-summary"

import { BaseApi, BaseApiRequestConfig } from "./base-api"

export class ProactiveAgentsApi extends BaseApi {
  async list(params?: {
    created_by?: "all" | "user_created" | "auto_created"
    page?: number
    per_page?: number
  }): Promise<ProactiveAgentListResponse> {
    const qs = params
      ? new URLSearchParams(
          Object.entries(params)
            .filter(([, value]) => value !== undefined)
            .map(([key, value]) => [key, String(value)])
        ).toString()
      : ""
    return this.get<ProactiveAgentListResponse>(
      `/api/proactive_agents${qs ? `?${qs}` : ""}`
    )
  }

  async show(id: number): Promise<AgentSummary> {
    return this.get<AgentSummary>(`/api/proactive_agents/${id}`)
  }

  async createAgent(
    payload: CreateHotelPriceAlertPayload
  ): Promise<AgentSummary> {
    return this.post<AgentSummary, CreateHotelPriceAlertPayload>(
      "/api/proactive_agents",
      payload
    )
  }

  async update(
    id: number,
    payload: UpdateProactiveAgentPayload
  ): Promise<AgentSummary> {
    return this.patch<AgentSummary, UpdateProactiveAgentPayload>(
      `/api/proactive_agents/${id}`,
      payload
    )
  }

  async createFlightAlert(
    payload: CreateFlightStatusAlertPayload
  ): Promise<AgentSummary> {
    return this.post<AgentSummary, CreateFlightStatusAlertPayload>(
      "/api/proactive_agents",
      payload
    )
  }

  async destroy(id: number): Promise<void> {
    return this.delete<void>(`/api/proactive_agents/${id}`)
  }

  async destroyAll(createdBy: "user_created" | "auto_created"): Promise<void> {
    return this.delete<void>(`/api/proactive_agents?created_by=${createdBy}`)
  }

  /**
   * Pause or resume every alert from one fuzzy search at once — a search can
   * produce ten of them, and acting on each in turn is what makes people give up
   * and switch the feature off.
   */
  async updateGroup(
    sourceIntentKey: string,
    status: ProactiveAgentStatus
  ): Promise<void> {
    return this.patch<void, { status: ProactiveAgentStatus }>(
      `/api/proactive_agent_groups/${encodeURIComponent(sourceIntentKey)}`,
      { status }
    )
  }

  async destroyGroup(sourceIntentKey: string): Promise<void> {
    return this.delete<void>(
      `/api/proactive_agent_groups/${encodeURIComponent(sourceIntentKey)}`
    )
  }

  async listRuns(id: number): Promise<ProactiveAgentRunsResponse> {
    return this.get<ProactiveAgentRunsResponse>(
      `/api/proactive_agents/${id}/runs`
    )
  }

  async currentPrice(
    params: {
      entity_ref?: string
      check_in: string
      check_out: string
      adults: number
      children?: number
      child_ages?: number[]
      currency?: string
      hotel_room_entity_ref?: string
      provider?: string
      provider_room_id?: string
    },
    customConfig?: BaseApiRequestConfig
  ): Promise<CurrentHotelPriceResponse> {
    const qs = new URLSearchParams(
      Object.entries(params)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)])
    ).toString()
    return this.get<CurrentHotelPriceResponse>(
      `/api/proactive_agents/current_price?${qs}`,
      customConfig
    )
  }
}
